// Settings → Edit message template (Wave E).
//
// Single-template editor: subject + plain-text body + HTML body, plus a
// variables hint sidebar showing every placeholder the template knows
// about. Subject is hidden for SMS / push templates.

import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Input } from '../../../components/form/Input';
import { Textarea } from '../../../components/form/Textarea';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { Badge } from '../../../components/ui/Badge/Badge';
import { Switch } from './components/Switch';
import { useTemplateDetail, useUpdateTemplate } from './hooks/useTemplates';
import type { UpdateTemplateInput } from '../../../types/api';
import styles from './TemplateEditPage.module.css';

export function TemplateEditPage(): JSX.Element {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const detail = useTemplateDetail(id);
  const update = useUpdateTemplate(id ?? '');

  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (!detail.data) return;
    setName(detail.data.template_name);
    setSubject(detail.data.subject ?? '');
    setBodyText(detail.data.body_text ?? '');
    setBodyHtml(detail.data.body_html ?? '');
    setIsActive(detail.data.is_active);
  }, [detail.data]);

  if (detail.isLoading || !detail.data) {
    return (
      <div className={styles.page}>
        <PageHeader title="Edit Template" />
        <Card>
          <div className={styles.empty}>
            <Spinner />
          </div>
        </Card>
      </div>
    );
  }

  const template = detail.data;
  const isEmail = template.channel_type === 'email';
  const variables = template.available_variables ?? [];

  function copyVariable(name: string) {
    const placeholder = `{{${name}}}`;
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(placeholder);
    }
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const patch: UpdateTemplateInput = {};
    if (name !== template.template_name) patch.template_name = name.trim();
    if (subject !== (template.subject ?? '')) patch.subject = subject || null;
    if (bodyText !== (template.body_text ?? '')) patch.body_text = bodyText || null;
    if (bodyHtml !== (template.body_html ?? '')) patch.body_html = bodyHtml || null;
    if (isActive !== template.is_active) patch.is_active = isActive;
    if (Object.keys(patch).length === 0) {
      navigate('/settings/templates');
      return;
    }
    update.mutate(patch, {
      onSuccess: () => {
        navigate('/settings/templates');
      },
    });
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title={template.template_name}
        subtitle={`${template.channel_type.toUpperCase()} · ${template.template_key}`}
        actions={
          <Button
            variant="ghost"
            icon={ArrowLeft}
            onClick={() => navigate('/settings/templates')}
          >
            Back
          </Button>
        }
      />

      <div className={styles.layout}>
        <Card title="Template Body" className={styles.bodyCard}>
          <form onSubmit={handleSubmit} className={styles.form}>
            <Input
              label="Template Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            {isEmail ? (
              <Input
                label="Subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                helper="Use {{variable_name}} placeholders for substitution."
              />
            ) : null}
            <Textarea
              label="Plain-text Body"
              value={bodyText}
              onChange={(e) => setBodyText(e.target.value)}
              rows={6}
              helper={
                isEmail
                  ? 'Falls back when the recipient mail client cannot render HTML.'
                  : 'The body sent over the channel.'
              }
            />
            {isEmail ? (
              <Textarea
                label="HTML Body"
                value={bodyHtml}
                onChange={(e) => setBodyHtml(e.target.value)}
                rows={10}
              />
            ) : null}
            <div className={styles.activeBlock}>
              <Switch
                checked={isActive}
                onChange={setIsActive}
                ariaLabel="Active"
                label={isActive ? 'Active' : 'Inactive'}
              />
              <p className={styles.activeHelp}>
                When disabled, this notification will not be sent to customers.
              </p>
            </div>
            <div className={styles.footer}>
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate('/settings/templates')}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                icon={Save}
                loading={update.isPending}
              >
                Save Template
              </Button>
            </div>
          </form>
        </Card>

        <Card title="Available Variables" className={styles.varsCard}>
          {variables.length === 0 ? (
            <p className={styles.muted}>
              No variables registered for this template.
            </p>
          ) : (
            <ul className={styles.varList}>
              {variables.map((v) => (
                <li key={v}>
                  <button
                    type="button"
                    className={styles.varBtn}
                    onClick={() => copyVariable(v)}
                    title={`Copy {{${v}}} to clipboard`}
                  >
                    <code>{`{{${v}}}`}</code>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <p className={styles.muted}>
            Click a variable to copy its placeholder. The server substitutes
            them at send time.
          </p>
          {template.is_default ? (
            <p>
              <Badge variant="info">Default template</Badge>
            </p>
          ) : null}
        </Card>
      </div>
    </div>
  );
}
